/**
 * NoteAuditCard Component
 *
 * Displays audit information for a note in the admin-v2 note detail page
 * Includes revision history timeline and compliance rules table
 */

import { useMemo } from 'react';
import {
  Assessment,
  CheckCircle,
  Error as ErrorIcon,
  History,
  Rule,
  Warning,
} from '@mui/icons-material';
import { Alert, Card, CardContent, CardHeader, Chip, Skeleton } from '@mui/material';

import {
  useGetAdminNoteAuditDetailQuery,
  useGetAdminNoteAuditHistoryQuery,
} from '@features/jotaudit/store/api';
import { TRIGGER_LABELS, QUALITY_COLORS } from '@features/jotaudit/constants';
import { RevisionTimeline } from '@features/jotaudit/components/history/RevisionTimeline';
import { RuleComplianceTable } from '@features/jotaudit/components/notes/RuleComplianceTable';
import { useGetRulesQuery } from '@features/rules/store/api';
import type {
  AuditTrigger,
  AuditRevisionSummary,
  RuleComplianceAssessment,
} from '@features/jotaudit/types';
import type { ComplianceRuleDocument } from '@features/rules/types';

interface NoteAuditCardProps {
  noteId: string;
}

/**
 * Format datetime to readable string
 */
const formatDateTime = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

/**
 * Get quality score color
 */
const getQualityColor = (score: number | null | undefined): string => {
  if (score === null || score === undefined) return '#6B7280';
  const roundedScore = Math.round(score);
  return QUALITY_COLORS[roundedScore] || '#6B7280';
};

export function NoteAuditCard({ noteId }: NoteAuditCardProps) {
  const { data, isLoading, error } = useGetAdminNoteAuditDetailQuery({ noteId });

  // Fetch revision history
  const { data: historyData, isLoading: isHistoryLoading } = useGetAdminNoteAuditHistoryQuery(
    { noteId },
    { skip: !noteId },
  );

  // Parse audit_data JSON
  const auditData = useMemo(() => {
    if (!data?.current_revision?.audit_data) return null;
    try {
      const raw = data.current_revision.audit_data;
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch {
      return null;
    }
  }, [data]);

  // Get compliance assessments from audit data
  const complianceAssessments: RuleComplianceAssessment[] = useMemo(() => {
    return auditData?.rule_compliance_assessments || [];
  }, [auditData]);

  // Fetch all compliance rules for the table
  const { data: rulesData, isLoading: isRulesLoading } = useGetRulesQuery(
    {
      rule_type: 'compliance',
      per_page: 100,
      page: 1,
    },
    { skip: complianceAssessments.length === 0 },
  );

  // Create a map of rule_id -> rule data for quick lookup
  const rulesMap = useMemo(() => {
    if (!rulesData?.items) return new Map<string, ComplianceRuleDocument>();

    const map = new Map<string, ComplianceRuleDocument>();
    rulesData.items.forEach(item => {
      if (item.rule_type === 'compliance' && 'body_md' in item.rule) {
        map.set(item.rule_id, item.rule as ComplianceRuleDocument);
      }
    });
    return map;
  }, [rulesData]);

  // Sort revisions by revision number (newest first)
  const revisions: AuditRevisionSummary[] = useMemo(() => {
    const list = historyData?.revisions || [];
    return [...list].sort((a, b) => b.revision - a.revision);
  }, [historyData]);

  if (isLoading) {
    return (
      <Card className="mb-6">
        <CardHeader
          title={
            <div className="flex items-center">
              <Assessment className="mr-2 h-5 w-5" />
              <span>Audit Information</span>
            </div>
          }
        />
        <CardContent>
          <div className="space-y-4">
            <Skeleton variant="rectangular" height={60} />
            <Skeleton variant="rectangular" height={100} />
            <Skeleton variant="rectangular" height={200} />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data?.audit) {
    return (
      <Card className="mb-6">
        <CardHeader
          title={
            <div className="flex items-center">
              <Assessment className="mr-2 h-5 w-5" />
              <span>Audit Information</span>
            </div>
          }
        />
        <CardContent>
          <p className="text-sm text-gray-500">No audit data available for this note.</p>
        </CardContent>
      </Card>
    );
  }

  const { audit, current_revision: revision, patient_name, provider_name } = data;
  const trigger = revision?.trigger as AuditTrigger | undefined;
  const triggerLabel = trigger ? TRIGGER_LABELS[trigger] || trigger : '—';
  const qualityScore = revision?.overall_quality_score;
  const compliancePercentage = revision?.compliance_percentage;

  return (
    <Card className="mb-6">
      <CardHeader
        title={
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Assessment className="mr-2 h-5 w-5 text-blue-600" />
              <span>Audit Information</span>
            </div>
            <Chip label={triggerLabel} size="small" variant="outlined" color="primary" />
          </div>
        }
      />
      <CardContent>
        {/* CPT Mismatch Warning */}
        {revision?.cpt_match === false && (
          <Alert severity="warning" icon={<Warning />} className="mb-4">
            <strong>CPT Code Mismatch:</strong> Provider code
            {(revision.provider_cpt_codes?.length ?? 0) > 1 ? 's' : ''} (
            {revision.provider_cpt_codes?.join(', ') || '—'}) differs from AI recommendation (
            {revision.recommended_cpt_codes?.join(', ') || '—'})
          </Alert>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {/* Quality Score */}
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <p className="text-xs text-gray-500 mb-1">Quality Score</p>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold" style={{ color: getQualityColor(qualityScore) }}>
                {qualityScore !== null && qualityScore !== undefined
                  ? qualityScore.toFixed(1)
                  : '—'}
              </span>
              <span className="text-sm text-gray-500">/ 5</span>
            </div>
          </div>

          {/* Compliance */}
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <p className="text-xs text-gray-500 mb-1">Compliance</p>
            <span
              className="text-2xl font-bold"
              style={{
                color:
                  compliancePercentage !== null && compliancePercentage !== undefined
                    ? compliancePercentage >= 80
                      ? '#22C55E'
                      : compliancePercentage >= 60
                        ? '#EAB308'
                        : '#EF4444'
                    : '#6B7280',
              }}
            >
              {compliancePercentage !== null && compliancePercentage !== undefined
                ? `${compliancePercentage.toFixed(0)}%`
                : '—'}
            </span>
          </div>

          {/* Total Flags */}
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <p className="text-xs text-gray-500 mb-1">Total Flags</p>
            <span className="text-2xl font-bold text-gray-900">
              {revision?.total_flags_count || 0}
            </span>
          </div>

          {/* Revision */}
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <p className="text-xs text-gray-500 mb-1">Revision</p>
            <span className="text-2xl font-bold text-gray-900">
              {revision?.revision || 1} of {audit?.total_revisions || 1}
            </span>
          </div>
        </div>

        {/* CPT Codes */}
        <div className="mb-6">
          <p className="text-sm font-medium text-gray-700 mb-2">CPT Codes</p>
          <div className="flex flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Provider:</span>
              {revision?.provider_cpt_codes?.map((code: string) => (
                <Chip key={code} label={code} size="small" color="primary" variant="outlined" />
              )) || <span className="text-sm text-gray-400">None</span>}
            </div>
            <div className="flex items-center gap-2 ml-4">
              <span className="text-xs text-gray-500">AI Recommended:</span>
              {revision?.recommended_cpt_codes?.map((code: string) => (
                <Chip key={code} label={code} size="small" color="secondary" variant="outlined" />
              )) || <span className="text-sm text-gray-400">None</span>}
            </div>
            {revision?.cpt_match !== null && revision?.cpt_match !== undefined && (
              <div className="ml-auto">
                {revision.cpt_match ? (
                  <Chip
                    icon={<CheckCircle />}
                    label="Match"
                    size="small"
                    color="success"
                    variant="filled"
                  />
                ) : (
                  <Chip
                    icon={<ErrorIcon />}
                    label="Mismatch"
                    size="small"
                    color="error"
                    variant="filled"
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Two Column Layout: Revision History & Compliance Rules */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Revision History */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <History className="h-5 w-5 text-gray-600" />
              <p className="text-sm font-medium text-gray-700">
                Revision History ({revisions.length})
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 max-h-80 overflow-y-auto">
              {isHistoryLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="flex gap-2">
                      <Skeleton variant="circular" width={12} height={12} />
                      <div className="flex-1">
                        <Skeleton width="60%" height={16} />
                        <Skeleton width="40%" height={14} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : revisions.length > 0 ? (
                <RevisionTimeline revisions={revisions} />
              ) : (
                <p className="text-sm text-gray-500">No revision history available</p>
              )}
            </div>
          </div>

          {/* Metadata */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Assessment className="h-5 w-5 text-gray-600" />
              <p className="text-sm font-medium text-gray-700">Audit Details</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="grid grid-cols-1 gap-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Last Audit:</span>
                  <span className="text-gray-900 font-medium">
                    {formatDateTime(audit.last_audit_at)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Provider:</span>
                  <span className="text-gray-900 font-medium">{provider_name || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Patient:</span>
                  <span className="text-gray-900 font-medium">{patient_name || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Note Title:</span>
                  <span className="text-gray-900 font-medium truncate max-w-[200px]">
                    {audit.note_title || '—'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Audit ID:</span>
                  <span className="text-gray-900 font-mono text-xs">
                    {audit.note_audit_id?.slice(0, 8) || '—'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Rule Compliance Table - Full Width */}
        {complianceAssessments.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Rule className="h-5 w-5 text-gray-600" />
              <p className="text-sm font-medium text-gray-700">
                Rule Compliance ({complianceAssessments.length} rules)
              </p>
            </div>
            <div className="max-h-96 overflow-y-auto">
              <RuleComplianceTable
                assessments={complianceAssessments}
                rulesMap={rulesMap}
                isLoadingRules={isRulesLoading}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default NoteAuditCard;

